import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { PaginationViewControl } from "@/Common/Components/Pagination/PaginationViewControl";
import { SlideMenuBar } from "@/Common/Components/Sidebar/SlideMenuBar";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { PharmacyOrderStatus, SortDirection } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ExternalMessageFormPopupComponent } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormPopupComponent";
import { Button, Icon } from "@/DesignSystem";
import { Tag } from "@/DesignSystem/Molecules/Tag/Tag";
import { PatientBannerForLabs } from "@/Labs/Components/PatientBannerForLabs";
import { MedicalSummaryPanelAction } from "@/Patient/Actions/MedicalSummaryPanelAction";
import { MedicalReactionsView } from "@/Patient/Components/MedicalItems/MedicalReactionsView";
import { MedicalSummaryPanel } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanel";
import { MedicalSummaryPanelContext } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { VitalSignsView } from "@/Patient/Components/VitalSigns/VitalSignsView";
import { MedicalItemHelper } from "@/Patient/Helpers/MedicalItemHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { PrescriptionRenewalsViewRenewMedicationTable } from "@/PrescriptionRenewals/Components/PrescriptionRenewalsViewRenewMedicationTable";
import { PharmacyOrdersClient } from "../Api/PharmacyOrdersClient";
import { PharmacyOrderHelper } from "../Helper/PharmacyOrderHelper";
import usePharmacyOrderPagination from "../Hooks/usePharmacyOrdersViewPagination";
import { PharmacyOrderMedicationModel } from "../Models/PharmacyOrderMedicationModel";
import { PharmacyOrderModel } from "../Models/PharmacyOrderModel";
import { PharmacyOrderBasicInfo } from "./PharmacyOrderBasicInfo";

import styles from "./PharmacyOrdersView.module.scss";

import CheckRegular from "@/DesignSystem/Atoms/Icons/Types/Checks/checks-regular.svg";
import Printer from "@/DesignSystem/Atoms/Icons/Types/Printer/printer-fill.svg";
import WarningRegular from "@/DesignSystem/Atoms/Icons/Types/Warning/warning-regular.svg";

type ExternalCommunicationVisibleTypes = "CLOSED" | "OPEN_WITH_EMPLOYEE" | "OPEN_WITHOUT_EMPLOYEE";

const emptyValue = "--";

function PharmacyOrdersView(): JSX.Element {
    const { getText } = useTranslation();

    const { orderId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [patientBannerModel, setPatientBannerModel] = useState<IPatientBannerModel | null>(null);
    const [pharmacyOrderModel, setPharmacyOrderModel] = useState<PharmacyOrderModel>(null);
    const [patientMedicalSummaryModel, setPatientMedicalSummaryModel] = useState<IPatientMedicalSummaryModel | null>(null);
    const [medicationModels, setMedicationModels] = useState<MedicationModel[]>([]);
    const [externalCommunicationVisibleType, setExternalCommunicationVisibleType] = useState<ExternalCommunicationVisibleTypes>("CLOSED");
    const [isMedicalSummaryPanelOpen, setIsMedicalSummaryPanelOpen] = useState<boolean>(cacheStore?.UserSettings.PharmacyOrdersMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? true);
    const [isAllergiesAndReactionsExpanded, setIsAllergiesAndReactionsExpanded] = useState<boolean>(pharmacyOrdersStore.isAllergiesAndReactionsExpanded ?? true);
    const [forceRefresh, setForceRefresh] = useState<boolean>(false);

    useEffect(() => {
        let active = true;

        const resetStates = (): void => {
            setPharmacyOrderModel(null);
            setPatientBannerModel(null);
            setPatientMedicalSummaryModel(null);
            setMedicationModels([]);
        };

        const fetchPharmacyOrder = async (): Promise<void> => {
            resetStates();
            setIsLoading(true);
            const pharmacyOrder = await PharmacyOrdersClient.getPharmacyOrderByOrderId(orderId);

            if (active) {
                sessionStore.dispatch(LocalOperations.PharmacyOrders.SetCurrentPharmacyOrderId, { orderId });
                setIsLoading(false);
                if (!pharmacyOrder) {
                    resetStates();
                    return;
                }

                setPatientBannerModel(
                    pharmacyOrder.Patient ?
                        Immutable.fromJS({
                            Model: Patient.fromResponseData(pharmacyOrder.Patient),
                            PatientDropdownVisible: false,
                            Warnings: []
                        }) : null
                );
                setPatientMedicalSummaryModel(
                    pharmacyOrder.CumulativePatientProfile ?
                        PatientMedicalSummaryHelper.getImmutableMedicalSummaryFromResponse(
                            pharmacyOrder.CumulativePatientProfile,
                            null
                        ) : null
                );

                const pharmacyOrderModel = PharmacyOrderModel.fromResponseModel(pharmacyOrder.PharmacyOrder);
                setPharmacyOrderModel(pharmacyOrderModel);
                setMedicationModels(
                    pharmacyOrderModel
                        .medications
                        ?.map(PharmacyOrderMedicationModel.toMedicationModel)
                        .sortByPropertyDesc(p => p.Model.pharmacyOrderMedicationStatus ?? Number.MIN_SAFE_INTEGER) ?? []
                );
            }
        };

        fetchPharmacyOrder();

        return () => {
            active = false;
        };
    }, [orderId, forceRefresh]);

    useEffect(() => {
        let active = true;

        const loadRamq = async (): Promise<void> => {
            if (!patientBannerModel) {
                return;
            }

            const patient = new Patient({ ...patientBannerModel.get("Model") });
            if (patient.RAMQInfo) {
                return;
            }

            await PatientBannerHelper.loadRamqInfo(patient);

            if (active) {
                setPatientBannerModel(p => p.set("Model", patient));
            }
        };

        loadRamq();

        return () => {
            active = false;
        };
    }, [patientBannerModel]);

    const latestVitalSigns = useMemo(
        () => MedicalItemHelper.getLatestVitalSign(patientMedicalSummaryModel?.get("MedicalItemsVitalSign")),
        [patientMedicalSummaryModel]
    );

    const medicalReactions = useMemo(
        () => MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(patientMedicalSummaryModel?.get("MedicalItemsMedicalReaction").toArray() ?? []),
        [patientMedicalSummaryModel]
    );

    const {
        totalPharmacyOrders,
        isPreviousButtonDisabled,
        absoluteIndex,
        onPreviousPharmacyOrder,
        onNextPharmacyOrder
    } = usePharmacyOrderPagination(orderId);

    const toggleMedicalSummaryPanel = (): void => {
        const isPanelOpen = !isMedicalSummaryPanelOpen;
        setIsMedicalSummaryPanelOpen(isPanelOpen);

        MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
            cacheStore?.UserSettings?.PharmacyOrdersMedicalSummaryPanelSettings,
            isPanelOpen,
            MedicalSummaryPanelContext.PharmacyOrders
        );
    };

    const toggleAlergiesAndReactions = (): void => {
        setIsAllergiesAndReactionsExpanded(p => !p);
    };

    const isClericalUser = sessionStore.getCurrentEmployee().isClerical();

    const burguerButtonCollapsible = (className: string, onClick: () => void): JSX.Element => (
        <button onClick={() => onClick()} className={className}>
            <SlideMenuBar />
        </button>
    );

    const handlePrintPreview = async (): Promise<void> => {
        const fileName = (await PharmacyOrdersClient.generatePharmacyOrderPdfFileName(orderId))?.FileName ?? "";
        if (fileName)
            PrintHelper.ShowPrintPreviewFileName(fileName);
    };

    const getAllergiesTag = (isAllergiesUpdated?: boolean): JSX.Element => {
        if (isAllergiesUpdated === false)
            return (
                <Tag label={getText("PharmacyOrders_NotUpToDateLabel")} variant="warning" size="sm">
                    <WarningRegular />
                </Tag>
            );

        if (isAllergiesUpdated === true)
            return (
                <Tag
                    iconPosition="left"
                    label={getText("PharmacyOrders_UpToDateLabel")}
                    size="sm"
                    theme="light"
                    variant="success"
                >
                    <CheckRegular />
                </Tag>
            );

        return null;
    };

    const filePharmacyOrder = async (): Promise<void> => {
        const isPharmacyOrderFiled = await PharmacyOrdersClient.filePharmacyOrder(orderId);

        if (isPharmacyOrderFiled) {
            displayToast(MessageToast.Success, getText("PharmacyOrders_OrderHasBeenFiledLabel"));
            onNextPharmacyOrder();
        }
    };

    const openOrderPatientForm = async (orderId: string): Promise<void> => {
        const orderFormResponse = await PharmacyOrdersClient.getOrderFormUrlAsync(orderId);
        const orderFormUrl = orderFormResponse?.PatientFormUrl;
        const isOrderSubmitted = orderFormResponse?.IsOrderSubmitted;

        if (isOrderSubmitted) {
            setForceRefresh(p => !p);
            return;
        }

        if (!orderFormUrl)
            return;

        window.open(orderFormUrl);
    };

    useEffect(() => {
        sessionStore.dispatch(LocalOperations.PharmacyOrders.SetAllergiesAndReactionsTogglerView, { isAllergiesAndReactionsExpanded });
    }, [isAllergiesAndReactionsExpanded]);

    return (
        <>
            <div className={styles.container}>
                <div className={`${styles.header} myle-page-header bottom-border`}>
                    {patientBannerModel && (
                        <PatientBannerForLabs
                            Model={patientBannerModel}
                            shouldLoadLastUpcomingAppointment
                            shouldHideMemo
                            shouldHideActivationLabel
                            shouldHideChangePicture
                            shouldHideUpdateInformation
                            isCurrentEmployeeActiveSiteInBritishColumbia={sessionStore.getCurrentEmployee().getActiveSite().isSiteInProvince(CanadaState.BritishColumbia)}
                            onDropDownMenuClick={() => {
                                setPatientBannerModel(p => p
                                    .set("PatientDropdownVisible", !p.get("PatientDropdownVisible"))
                                );
                            }}
                            onNewExternalCommunicationClick={() => setExternalCommunicationVisibleType("OPEN_WITHOUT_EMPLOYEE")}
                            onTaskOrMessageClick={(patient: Person, isTask: boolean) => {
                                sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
                                    patient,
                                    isTask
                                });
                            }}
                        />
                    )}
                </div>
                <div className={styles.mainContainer}>
                    <div className={styles.ordersContainer}>
                        <div className={styles.subHeader}>
                            {!isLoading && (
                                <>
                                    <PharmacyOrderBasicInfo pharmacyOrderInfo={pharmacyOrderModel} />
                                    <div className={`${styles.medicationReactionsViewContainer} ${isAllergiesAndReactionsExpanded ? styles.open : ""}`}>
                                        {isAllergiesAndReactionsExpanded && (
                                            <MedicalReactionsView
                                                reactions={medicalReactions as MedicalItemMedicalReaction[] ?? []}
                                                element={getAllergiesTag(pharmacyOrderModel?.isAllergiesUpdated)}
                                                showAllergiesEmptyLabel
                                                showIntolerancesEmptyLabel
                                                showReactionsEmptyLabel
                                            />
                                        )}
                                    </div>
                                    {burguerButtonCollapsible(styles.slideHorizontalBar, () => toggleAlergiesAndReactions())}
                                    <VitalSignsView
                                        currentPatient={patientBannerModel?.get("Model")}
                                        cssClassName={styles.vitalSign}
                                        vitalSign={latestVitalSigns}
                                        showArrow={false}
                                        hideVitalSignLabel
                                    />
                                </>
                            )}
                        </div>
                        <div className={styles.main}>
                            {!isLoading && (
                                <>
                                    <div className={styles.navbarContainer}>
                                        <span className={`${styles.navbarLink} ${styles.active}`}>{getText("PharmacyOrders_OrderLabel")}</span>
                                    </div>
                                    <div className={styles.orderInfoActionContainer}>
                                        <div className={styles.infoContainer}>
                                            <div>
                                                <span className={styles.label}>{`${getText("PharmacyOrders_OrderedByLabel")}:`}</span>
                                                <span className="margin-sm-right">{`${pharmacyOrderModel?.prescriberName ?? emptyValue} (${pharmacyOrderModel?.prescriberLicenseNumber ? pharmacyOrderModel.prescriberLicenseNumber : emptyValue})`}</span>
                                                <a onClick={() => setExternalCommunicationVisibleType("OPEN_WITH_EMPLOYEE")}>{getText("NewMessageLabel")}</a>
                                            </div>
                                            <div>
                                                <span className={styles.label}>{`${getText("NoteDLabel")}:`}</span>
                                                <span>{pharmacyOrderModel?.prescriptionNotes ? pharmacyOrderModel.prescriptionNotes : emptyValue}</span>
                                            </div>
                                        </div>
                                        <button title={getText("PrintFilesLabel")} className={styles.printerButton} onClick={handlePrintPreview}>
                                            <Icon size="md">
                                                <Printer />
                                            </Icon>
                                        </button>
                                    </div>
                                    <div className={styles.medicationTableContainer}>
                                        <PrescriptionRenewalsViewRenewMedicationTable
                                            medicationModel={medicationModels}
                                            defaultOrderDirection={SortDirection.DESC}
                                            onSortMedications={newMedicationModels => setMedicationModels(newMedicationModels)}
                                            shouldDisableAction
                                            hasStatusColumn
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {!isClericalUser && !isLoading && (
                        <div className={`${styles.slideMenuSummaryPanel} ${isMedicalSummaryPanelOpen ? styles.open : ""}`}>
                            {burguerButtonCollapsible(styles.slideBar, () => toggleMedicalSummaryPanel())}
                            {isMedicalSummaryPanelOpen && pharmacyOrderModel?.patientContactId && (
                                <div className={styles.summaryPanelContainer}>
                                    {pharmacyOrderModel.userConsentedSharingMedicalItems === false && (
                                        <div className={styles.noConsentToShareMedicalItemContainer}>
                                            {getText("PharmacyOrders_NoUserConsentToShareMedicalItemsLabel")}
                                        </div>
                                    )}
                                    <div className={styles.medicalSummaryLabel}>
                                        {getText("MedicalSummaryMLabel")}
                                    </div>
                                    <MedicalSummaryPanel
                                        patientContactId={pharmacyOrderModel.patientContactId}
                                        context={MedicalSummaryPanelContext.PharmacyOrders}
                                        cssClass="containerPharmacyOrders"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.footer}>
                    <div className={styles.pagination}>
                        <PaginationViewControl
                            itemCount={totalPharmacyOrders}
                            isPreviousButtonDisabled={isPreviousButtonDisabled}
                            absoluteIndex={absoluteIndex}
                            onPreviousItemClick={() => onPreviousPharmacyOrder()}
                            onNextItemClick={() => onNextPharmacyOrder()}
                        />
                    </div>
                    <div className={styles.actionButtons}>
                        <Button
                            label={getText("GoBackLabel")}
                            dataCy="order-view-go-back-button"
                            variant="neutral"
                            category="secondary"
                            onClick={async () => navigate("/orders")}
                        />
                        {cacheStore.ClinicSettings.EnablePharmacistCoordinatePrescription && pharmacyOrderModel
                        && (pharmacyOrderModel.orderStatus === PharmacyOrderStatus.Contacted || pharmacyOrderModel.orderStatus === PharmacyOrderStatus.NotContacted)
                        && (
                            <Button
                                label={getText("PharmacyOrders_AnswerFormLabel")}
                                dataCy="order-view-answer-form"
                                variant="brand"
                                category="secondary"
                                onClick={async () => await openOrderPatientForm(orderId)}
                            />
                        )
                        }
                        {pharmacyOrderModel && pharmacyOrderModel.orderStatus === PharmacyOrderStatus.Contacted && !pharmacyOrderModel.filedDate && (
                            <Button
                                label={getText("PharmacyOrders_FileLabel")}
                                dataCy="order-view-file-button"
                                variant="brand"
                                category="primary"
                                onClick={filePharmacyOrder}
                            />
                        )}
                        {pharmacyOrderModel && pharmacyOrderModel.orderStatus !== PharmacyOrderStatus.Contacted && (
                            <Button
                                label={getText("PharmacyOrders_ActionComplete")}
                                dataCy="order-view-complete-button"
                                variant="brand"
                                category="primary"
                                onClick={async () => {
                                    const sendNotification = PharmacyOrderHelper.hasSendNotification(pharmacyOrderModel.orderStatus);
                                    const response = await PharmacyOrdersClient.completePharmacyOrdersAsync([orderId], sendNotification);
                                    if (response) {
                                        displayToast(MessageToast.Success, getText("PharmacyOrders_MessageCompletedLabel"));
                                        onNextPharmacyOrder();
                                    }
                                }}
                                disabled={isLoading || pharmacyOrderModel.orderStatus == PharmacyOrderStatus.Completed || pharmacyOrderModel.orderStatus == PharmacyOrderStatus.Canceled}
                            />
                        )}
                    </div>
                </div>
            </div>
            {externalCommunicationVisibleType !== "CLOSED" && (
                <ExternalMessageFormPopupComponent
                    selectedPatient={patientBannerModel?.get("Model") ?
                        InternalPatientResponse.fromPerson(Person.FromPatient(patientBannerModel.get("Model"))) : null
                    }
                    selectedExternalEmployee={externalCommunicationVisibleType === "OPEN_WITH_EMPLOYEE" ? pharmacyOrderModel.prescriber : null}
                    onClose={() => setExternalCommunicationVisibleType("CLOSED")}
                    onSend={() => setExternalCommunicationVisibleType("CLOSED")}
                    selectedAttachments={[]}
                />
            )}
        </>
    );
}

export { PharmacyOrdersView };
